<script setup lang="ts">
import { type Ref, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import {
  type PreGetThemeResponse,
  type ThemeDTO,
  getThemes,
  selectTheme,
  type CreateMemorialThemeCommand,
  type ThemeCategory
} from '@/composables/memorize/memorize'
import { themeServiceId, selectedTheme, previewTheme, setSelectedThemeByServiceId } from '@/composables/theme/theme'
import { handleErr } from '@/composables/exception/handler'
import { ElMessage } from 'element-plus'
import type { CommonResponse } from '@/apis/api'
import { CODE_MEMBER_ONLY_THEME, CODE_OK } from '@/apis/code'
import { isEmpty, renderMessage, WAIT_FOR_LOADING_TIME } from '@/utils/util'
import { closeLoading, openLoading } from '../../loading/loading'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { currentUser, getUserProfile } from '@/composables/user/users'
// Import Swiper Vue.js components
import { Swiper, SwiperSlide } from 'swiper/vue'
// Import Swiper styles
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
// import required modules
import { Navigation, Pagination } from 'swiper/modules'
import type { Swiper as SwiperType } from 'swiper'
import { debounce } from 'perfect-debounce'
import { currentLang } from '@/composables/settings/storage'
import { useRouter } from 'vue-router'

const router = useRouter()

const { t } = useI18n()

const model = defineModel<boolean>()

const props = defineProps({
  isMember: {
    type: Boolean,
    required: true,
    default: false
  }
})

let innerWidth = ref(0)
if (typeof window !== 'undefined') {
  innerWidth.value = window.innerWidth
}

onMounted(() => {
  window.addEventListener('resize', (e) => debounced())
  nextTick(() => {
    getData()
  })
})

const debounced = debounce(() => {
  innerWidth.value = window.innerWidth
}, 100)

watch(model, (newVal) => {
  if (newVal) {
    getData()
  }
})

const themes: Ref<Array<ThemeDTO> | undefined> = ref()
const selectedThemeId: Ref<string | undefined> = ref()
const themeCategories = ref<Array<ThemeCategory>>([])

const dataState = ref('none')
function getData() {
  retriveThemes(themeServiceId.value)
  setTimeout(() => {
    if (dataState.value === 'none') {
      dataState.value = 'loading'
    }
  }, WAIT_FOR_LOADING_TIME)
}

async function retriveThemes(serviceId: string) {
  if (isEmpty(serviceId)) {
    return
  }
  const { data, error, statusCode } = await getThemes(serviceId)
  if (error.value) {
    //handleErr(data, error, statusCode)
    dataState.value = 'error'
  } else {
    const resData = data as unknown as Ref<CommonResponse<PreGetThemeResponse>>
    if (resData.value.code === CODE_OK) {
      // success
      const response = resData.value.data
      themes.value = response.themes.filter((t) => !t.outdated)
      selectedThemeId.value = response.selectedThemeId
      themeCategories.value = response.themeCategories

      dataState.value = 'done'
    } else {
      ElMessage({
        message: renderMessage(resData.value.message),
        type: 'warning'
      })
      dataState.value = 'error'
    }
  }
}

const emit = defineEmits(['onSelectTheme'])

function onPreviewTheme(theme: ThemeDTO) {
  ElMessage({
    message: renderMessage(t('alt.in_preview')),
    type: 'info',
    duration: 3000
  })
  // 修复点：使用 Object.assign 更新 previewTheme.value
  const updatedPreview = { ...previewTheme.value }
  updatedPreview[themeServiceId.value] = theme
  previewTheme.value = updatedPreview
}

async function setTheme(theme: ThemeDTO) {
  console.log('theme:', theme)

  openLoading(t('alt.setting'))
  const { data, error, statusCode } = await selectTheme({
    serviceId: themeServiceId.value,
    themeId: theme.id
  } as CreateMemorialThemeCommand)
  closeLoading()
  if (error.value) {
    handleErr(data, error, statusCode)
  } else {
    const resData = data as unknown as Ref<CommonResponse<any>>
    if (resData.value.code === CODE_OK) {
      // success
      setSelectedThemeByServiceId(themeServiceId.value, theme)
      delete previewTheme.value[themeServiceId.value]
      ElMessage({
        message: t('status.setting_done'),
        type: 'success'
      })
      model.value = false
    } else {
      if (CODE_MEMBER_ONLY_THEME === resData.value.code) {
        getUserProfile()
      }
      ElMessage({
        message: renderMessage(resData.value.message),
        type: 'warning'
      })
    }
  }
}

const selectedItem = ref<ThemeDTO | undefined>()

const groupedThemes = computed(() => {
  const result: ThemeDTO[][] = []
  if (themes.value && themes.value.length > 0) {
    const itemsPerPage = innerWidth.value < 768 ? 2 : 3
    const filteredGifts = themes.value?.filter((t) => !t.outdated && t.category === currentCategory.value)
    for (let i = 0; i < filteredGifts.length; i += itemsPerPage) {
      result.push(filteredGifts.slice(i, i + itemsPerPage))
    }
    selectedItem.value = filteredGifts[0]
  }
  return result
})

const modules = computed(() => {
  return innerWidth.value > 768 ? [Navigation, Pagination] : [Pagination]
})

const themeSwiper = ref<SwiperType | null>(null)
function onThemeSwiperInit(swiper: SwiperType) {
  themeSwiper.value = swiper
}

watch(groupedThemes, () => {
  nextTick(() => {
    themeSwiper.value?.slideTo(0)
  })
})

function onSetTheme() {
  if (isEmpty(previewTheme.value[themeServiceId.value])) {
    ElMessage({
      message: renderMessage(t('theme.select_one_pls')),
      type: 'info',
      duration: 3000
    })
    return
  }
  setTheme(previewTheme.value[themeServiceId.value])
}

const currentCategory = ref('traditional')

function onClose() {
  delete previewTheme.value[themeServiceId.value]
  ElMessage({
    message: renderMessage(t('theme.no_selected_theme')),
    type: 'info',
    duration: 3000
  })
  model.value = false
}

onUnmounted(() => {
  delete previewTheme.value[themeServiceId.value]
})

function toMember() {
  router.push('/member?why=theme')
}

// 在脚本部分定义类型断言帮助函数
const swiperOptions = {
  navigation: (width: number) => (width > 768 ? { enabled: true } : undefined) as any,
  pagination: { enabled: true, clickable: true } as any
}
</script>
<template>
  <RAction v-model="model" :backdropHide="false" :fromBottom="true" :mask="false" :expandable="true">
    <div flex="~ col" justify-center rounded-2 bg-gray-800 px-2 py-2 w-full class="safe-padding theme-picker">
      <div flex="~ row" mb-2 items-center px-2 pt-2>
        <h1 grow text-xl text-inverted>{{ $t('theme.please_select') }}</h1>
        <div p-1 icon-button @click="onClose">
          <div w-6 h-6 i-iconamoon:close-thin text-inverted></div>
        </div>
      </div>
      <el-scrollbar>
        <div class="flex flex-row">
          <p
            v-for="item in themeCategories"
            :key="item.value.key"
            text-inverted
            hover:text-third
            pl-2
            pr-4
            py-3
            text-base
            @click="currentCategory = item.value.key"
            :class="{ 'text-primary font-bold hover:text-primary-active': currentCategory === item.value.key }">
            {{ item.value.data[currentLang] }}
          </p>
        </div>
      </el-scrollbar>
      <ClientOnly>
        <swiper
          v-if="groupedThemes.length > 0"
          :slides-per-view="1"
          :navigation="swiperOptions.navigation(innerWidth)"
          :pagination="swiperOptions.pagination"
          :modules="modules"
          @swiper="onThemeSwiperInit"
          w-full>
          <swiper-slide w-auto v-for="(slide, slideIndex) in groupedThemes" :key="slideIndex">
            <div w-full h-42 px-2 pb-6 pt-2 grid grid-cols-2 md:grid-cols-3 gap-4 items-start justify-center>
              <ThemeItemView
                v-for="(theme, index) in slide"
                :key="theme.id"
                :selectedId="selectedThemeId || ''"
                :previewId="previewTheme[themeServiceId]?.id || ''"
                :theme="theme"
                @click.prevent.stop="onPreviewTheme(theme)" />
            </div>
          </swiper-slide>
        </swiper>
        <div v-else h-42 w-full flex-r-center>
          <span text-third>{{ $t('theme.no_theme') }}</span>
        </div>
      </ClientOnly>
      <div mt-6 mb-4 flex="~ row" justify-end items-center>
        <button
          v-if="props.isMember || !(previewTheme[themeServiceId]?.memberOnly || false)"
          btn-gray
          text-main
          mr-2
          py-2
          @click="onSetTheme">
          {{ $t('theme.save_theme') }}
        </button>
        <button
          v-if="!props.isMember && previewTheme[themeServiceId]?.memberOnly"
          btn-member-plan-adv
          mr-2
          py-1.8
          bg-vip
          text-main
          @click="toMember">
          {{ $t('theme.member_set_after_purchase') }}
        </button>
      </div>
    </div>
  </RAction>
</template>
<style lang="scss">
.theme-picker {
  --swiper-pagination-color: #ffffff;
  --swiper-theme-color: #ffffff;
}

.theme-picker .swiper-button-next,
.theme-picker .swiper-button-prev {
  color: var(--c-text-second) !important;
}

.theme-picker .swiper-button-disabled {
  color: var(--c-text-third) !important;
}

.theme-picker .swiper-pagination-bullet {
  background: #e9e9e9;
  opacity: 0.5;
}

/* 当前激活的分页点 */
.theme-picker .swiper-pagination-bullet-active {
  background: #ffffff !important;
  opacity: 1;
}

/* 增加分页器和幻灯片之间的间距 */
.theme-picker .swiper-pagination {
  position: absolute;
  bottom: 0px !important; /* 默认是10px，增大这个值 */
}

@supports (bottom: constant(safe-area-inset-bottom)) or (bottom: env(safe-area-inset-bottom)) {
  .safe-padding {
    padding-bottom: constant(safe-area-inset-bottom);
    padding-bottom: env(safe-area-inset-bottom);
    padding-top: 0.45rem;
    transition: padding-bottom 0.3s ease;
  }
}
</style>
